import moment from "moment";
import "moment-timezone";

const asRange = (start, end, label) => ({
  from: start.clone().toISOString(),
  to: end.clone().toISOString(),
  label,
});

export const getPeriodRanges = (period, timezone = "Asia/Riyadh") => {
  const now = moment().tz(timezone);
  let currentStart;
  let currentEnd;
  let previousStart;
  let previousEnd;
  let unit = "month";
  let labelFormat = "MMM";
  let trendAnchor = now.clone();

  switch (period) {
    case "Today":
      currentStart = now.clone().startOf("day");
      currentEnd = now.clone().endOf("day");
      previousStart = now.clone().subtract(1, "day").startOf("day");
      previousEnd = now.clone().subtract(1, "day").endOf("day");
      unit = "day";
      labelFormat = "DD MMM";
      break;
    case "Current Week":
      currentStart = now.clone().startOf("isoWeek");
      currentEnd = now.clone().endOf("day");
      previousStart = now.clone().subtract(1, "week").startOf("isoWeek");
      previousEnd = now.clone().subtract(1, "week").endOf("isoWeek");
      unit = "week";
      labelFormat = "DD MMM";
      break;
    case "Last Week":
      currentStart = now.clone().subtract(1, "week").startOf("isoWeek");
      currentEnd = now.clone().subtract(1, "week").endOf("isoWeek");
      previousStart = now.clone().subtract(2, "week").startOf("isoWeek");
      previousEnd = now.clone().subtract(2, "week").endOf("isoWeek");
      unit = "week";
      labelFormat = "DD MMM";
      trendAnchor = currentEnd.clone();
      break;
    case "Last Month":
      currentStart = now.clone().subtract(1, "month").startOf("month");
      currentEnd = now.clone().subtract(1, "month").endOf("month");
      previousStart = now.clone().subtract(2, "month").startOf("month");
      previousEnd = now.clone().subtract(2, "month").endOf("month");
      unit = "month";
      labelFormat = "MMM";
      trendAnchor = currentEnd.clone();
      break;
    case "YTD":
      currentStart = now.clone().startOf("year");
      currentEnd = now.clone().endOf("day");
      previousStart = now.clone().subtract(1, "year").startOf("year");
      previousEnd = now.clone().subtract(1, "year").endOf("year");
      unit = "month";
      labelFormat = "MMM";
      break;
    case "Last Year":
      currentStart = now.clone().subtract(1, "year").startOf("year");
      currentEnd = now.clone().subtract(1, "year").endOf("year");
      previousStart = now.clone().subtract(2, "year").startOf("year");
      previousEnd = now.clone().subtract(2, "year").endOf("year");
      unit = "month";
      labelFormat = "MMM";
      trendAnchor = currentEnd.clone();
      break;
    case "All":
      currentStart = moment.tz("2020-01-01", timezone).startOf("day");
      currentEnd = now.clone().endOf("day");
      previousStart = moment.tz("2020-01-01", timezone).startOf("day");
      previousEnd = now.clone().subtract(1, "year").endOf("day");
      unit = "month";
      labelFormat = "MMM";
      break;
    case "MTD":
    default:
      currentStart = now.clone().startOf("month");
      currentEnd = now.clone().endOf("day");
      previousStart = now.clone().subtract(1, "month").startOf("month");
      previousEnd = now.clone().subtract(1, "month").endOf("month");
      unit = "month";
      labelFormat = "MMM";
      break;
  }

  const current = asRange(
    currentStart,
    currentEnd,
    currentStart.clone().locale("en").format(labelFormat)
  );
  const previous = asRange(
    previousStart,
    previousEnd,
    previousStart.clone().locale("en").format(labelFormat)
  );

  const trend = [];

  // YTD: one bar per month from Jan → current month.
  if (period === "YTD") {
    const monthsCount = now.month() + 1;
    for (let i = 0; i < monthsCount; i += 1) {
      const start = now.clone().startOf("year").add(i, "month").startOf("month");
      let end = start.clone().endOf("month");
      if (i === monthsCount - 1) end = now.clone().endOf("day");
      trend.push(asRange(start, end, start.clone().locale("en").format("MMM")));
    }
    return { current, previous, trend };
  }

  // Last Year: full Jan → Dec of the previous calendar year.
  if (period === "Last Year") {
    const yearStart = now.clone().subtract(1, "year").startOf("year");
    for (let i = 0; i < 12; i += 1) {
      const start = yearStart.clone().add(i, "month").startOf("month");
      const end = start.clone().endOf("month");
      trend.push(asRange(start, end, start.clone().locale("en").format("MMM")));
    }
    return { current, previous, trend };
  }

  for (let offset = 5; offset >= 0; offset -= 1) {
    if (unit === "day") {
      const start = trendAnchor.clone().subtract(offset, "day").startOf("day");
      const end = trendAnchor.clone().subtract(offset, "day").endOf("day");
      trend.push(asRange(start, end, start.clone().locale("en").format(labelFormat)));
      continue;
    }
    if (unit === "week") {
      const start = trendAnchor.clone().subtract(offset, "week").startOf("isoWeek");
      let end = trendAnchor.clone().subtract(offset, "week").endOf("isoWeek");
      if (offset === 0 && period === "Current Week") end = now.clone().endOf("day");
      trend.push(asRange(start, end, start.clone().locale("en").format(labelFormat)));
      continue;
    }
    const start = trendAnchor.clone().subtract(offset, "month").startOf("month");
    let end = start.clone().endOf("month");
    if (offset === 0 && (period === "MTD" || period === "All" || !period)) {
      end = now.clone().endOf("day");
    }
    trend.push(asRange(start, end, start.clone().locale("en").format("MMM")));
  }

  return { current, previous, trend };
};
